import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import CreatableSelect from '@/components/CreatableSelect';
import { useOffice } from '@/hooks/useOffice';
import { logActivity } from '@/lib/activityLog';

interface Props {
  field: 'box_number' | 'location' | 'tape_type';
  options: string[];
  ids: number[];
  onClose: () => void;
  onDone: () => void;
}

export default function BatchUpdateModal({ field, options, ids, onClose, onDone }: Props) {
  const { currentOfficeId } = useOffice();
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const isBox = field === 'box_number';
  const labels: Record<string, string> = { box_number: 'Box Number', location: 'Location', tape_type: 'Tape Type' };
  const actionNames: Record<string, string> = { box_number: 'change_box_batch', location: 'change_location_batch', tape_type: 'change_tape_type_batch' };
  const label = labels[field];
  const actionName = actionNames[field];

  const handleChange = (v: string) => {
    setValue(v);
    setTouched(true);
  };

  const apply = async () => {
    if (!touched) { setErr(`Please choose a ${label.toLowerCase()} or clear it`); return; }
    const trimmed = value.trim();
    setSaving(true); setErr('');
    const payload: Record<string, string | null> = { [field]: trimmed || null, updated_at: new Date().toISOString() };
    const { error } = await supabase.from('tapes').update(payload).in('id', ids);
    if (error) { setSaving(false); setErr(error.message); return; }

    // Best-effort: when assigning a new box number, make sure that box exists
    // in the boxes table so the Boxes page shows it (and its tape count).
    if (isBox && trimmed) {
      try {
        const { data: existingBox } = await supabase
          .from('boxes')
          .select('id')
          .eq('box_number', trimmed)
          .eq('office_id', currentOfficeId)
          .maybeSingle();
        if (!existingBox) {
          await supabase.from('boxes').insert({
            box_number: trimmed,
            location: null,
            office_id: currentOfficeId,
          });
        }
      } catch {
        // Box sync is best-effort; don't block the tape update on it.
      }
    }

    setSaving(false);
    onDone();
    const detail = trimmed
      ? `Set ${label.toLowerCase()} to "${trimmed}" for ${ids.length} tape(s)`
      : `Cleared ${label.toLowerCase()} for ${ids.length} tape(s)`;
    await logActivity(actionName, detail);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-sm">Change {label}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 cursor-pointer">
            <i className="ri-close-line w-5 h-5 flex items-center justify-center" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-500">
            Update <strong className="text-slate-800">{ids.length}</strong> selected tape{ids.length === 1 ? '' : 's'} to the {label.toLowerCase()} below.
          </p>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">New {label}</label>
            <CreatableSelect
              value={value}
              onChange={handleChange}
              options={options}
              placeholder={`Select or add ${label}`}
              clearable
              emptyLabel="(None — clear)"
            />
          </div>
          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border border-slate-200 cursor-pointer whitespace-nowrap">Cancel</button>
          <button
            onClick={apply}
            disabled={saving || !touched}
            className="px-4 py-2 text-sm rounded-md bg-slate-900 text-white cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {saving ? 'Applying...' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}